"use client";

import "./article-tease-ris-extension.scss";

import { useState } from "react";
import { Truncator } from "design-system/components/blocks/truncator/truncator";
import { ArticleFooterTag } from "design-system/components/pages/article-page/article-footer/article-footer-tag";
import { CtaLink } from "design-system/components/primitives/cta-link/cta-link";
import { Link } from "../link/link";

export interface ArticleTeaseRisKeyword {
  label: string;
  link: string;
}

export interface ArticleTeaseRisCta {
  label: string;
  href: string;
  type: "primary-button" | "secondary-button";
}

export interface ArticleTeaseRisExtensionProps {
  // Publication keywords, rendered as article-footer tags. Each links to the
  // publications search (taxonomy fields → facet params, free-text → q).
  keywords?: Array<ArticleTeaseRisKeyword>;

  // Publication citation (HTML), shown under the "Citation:" label when open.
  citation?: string;

  // Publication CTAs from the indexed links (display.ctas). First is primary,
  // the rest secondary. Rendered after the author list inside the truncator.
  ctas?: Array<ArticleTeaseRisCta>;

  // Endpoint for the lazy "More from this author" list. Fetched client-side the
  // first time the truncator opens (props are SSR-serialized, so no callback can
  // be passed down). Returns Array<{ title, authors?, url }>. Omit to hide it.
  moreFromAuthorEndpoint?: string;

  // Pre-loaded "More from this author" items. When set, the list renders
  // immediately and no fetch happens (used by Storybook and when the data is
  // already available). Takes precedence over moreFromAuthorEndpoint.
  moreFromAuthor?: Array<RecentPublication>;
}

interface RecentPublication {
  title: string;
  authors?: Array<string>;
  url: string;
}

const TRUNCATOR_LABEL = "Citation & Related Publications";

type LoadState = "idle" | "loading" | "loaded" | "error";

function parseHtml(html: string) {
  return (
    <span dangerouslySetInnerHTML={{ __html: html.replace(/&nbsp;/g, " ") }} />
  );
}

// Best-effort "Last, First" → "First Last" (flip on the first comma).
function flipName(name: string) {
  const i = name.indexOf(",");
  return i > 0
    ? `${name.slice(i + 1).trim()} ${name.slice(0, i).trim()}`
    : name;
}

function formatAuthors(authors: Array<string>) {
  const names = authors.map(flipName);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

export function ArticleTeaseRisExtension({
  keywords,
  citation,
  ctas,
  moreFromAuthorEndpoint,
  moreFromAuthor,
}: ArticleTeaseRisExtensionProps) {
  const preloaded = moreFromAuthor && moreFromAuthor.length > 0;
  const [state, setState] = useState<LoadState>(preloaded ? "loaded" : "idle");
  const [recent, setRecent] = useState<Array<RecentPublication>>(
    moreFromAuthor ?? [],
  );

  async function loadOnce() {
    if (preloaded || state !== "idle" || !moreFromAuthorEndpoint) return;
    setState("loading");
    try {
      const res = await fetch(moreFromAuthorEndpoint);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setRecent(Array.isArray(data) ? data : []);
      setState("loaded");
    } catch {
      setState("error");
    }
  }

  const hasKeywords = keywords && keywords.length > 0;
  const hasMoreFromAuthor = !!moreFromAuthorEndpoint || preloaded;
  const hasTruncatorContent =
    citation || hasMoreFromAuthor || (ctas && ctas.length > 0);

  return (
    <div className="hbs-article-tease-ris">
      {hasKeywords && (
        <p className="hbs-article-tease-ris__row hbs-article-tease-ris__row--keywords">
          <span className="hbs-article-tease-ris__label">Keywords:</span>
          <span className="hbs-article-tease-ris__chips">
            {keywords.map((keyword, i) => (
              <ArticleFooterTag key={i} {...keyword} />
            ))}
          </span>
        </p>
      )}

      {hasTruncatorContent && (
        <Truncator
          labelHidden={`Show ${TRUNCATOR_LABEL}`}
          labelVisible={`Hide ${TRUNCATOR_LABEL}`}
          align="center"
          onOpenChange={(open) => open && loadOnce()}
        >
          {citation && (
            <div className="hbs-article-tease-ris__row">
              <span className="hbs-article-tease-ris__label">Citation:</span>
              <div className="hbs-article-tease-ris__body">
                {parseHtml(citation)}
              </div>
            </div>
          )}

          {hasMoreFromAuthor && (
            <div className="hbs-article-tease-ris__row hbs-article-tease-ris__row--more-from-author">
              <span className="hbs-article-tease-ris__label">
                More From This Author:
              </span>
              {state === "loading" && (
                <p className="hbs-article-tease-ris__status">Loading…</p>
              )}
              {state === "error" && (
                <p className="hbs-article-tease-ris__status">
                  Unable to load publications.
                </p>
              )}
              {state === "loaded" && recent.length > 0 && (
                <ul className="hbs-article-tease-ris__more-list">
                  {recent.map((pub, i) => (
                    <li key={i}>
                      <Link href={pub.url}>{pub.title}</Link>
                      {pub.authors && pub.authors.length > 0 && (
                        <> By: {formatAuthors(pub.authors)}</>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {ctas && ctas.length > 0 && (
            <div className="hbs-article-tease-ris__ctas">
              {ctas.map((cta, i) => (
                <CtaLink key={i} href={cta.href} type={cta.type} isSmall={true}>
                  {cta.label}
                </CtaLink>
              ))}
            </div>
          )}
        </Truncator>
      )}
    </div>
  );
}
